'use client';

import { useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';

const batches = [
  {
    level: 'Beginners',
    emoji: '🌱',
    age: '6 – 14 years',
    timing: 'Mon, Wed, Fri — 5:00 PM to 6:00 PM',
    startDate: 'March 1, 2026',
    fee: '₹2,500 / month',
    spots: 8,
    color: 'bg-accent/10 border-accent/30',
    badge: 'Popular',
    badgeColor: 'bg-accent text-dark',
    features: ['Basic Garba steps', 'Tali & Dodhiya patterns', 'Navratri songs introduction', 'Costume guidance'],
  },
  {
    level: 'Intermediate',
    emoji: '🔥',
    age: '12 – 25 years',
    timing: 'Tue, Thu, Sat — 6:30 PM to 8:00 PM',
    startDate: 'March 1, 2026',
    fee: '₹3,500 / month',
    spots: 5,
    color: 'bg-primary/8 border-primary/25',
    badge: 'Filling Fast',
    badgeColor: 'bg-primary text-white',
    features: ['Raas Garba technique', 'Dandiya choreography', 'Stage performance prep', 'Rhythm & expression'],
  },
  {
    level: 'Advanced',
    emoji: '🏆',
    age: '15+ years',
    timing: 'Mon to Sat — 7:30 AM to 9:00 AM',
    startDate: 'March 5, 2026',
    fee: '₹5,000 / month',
    spots: 3,
    color: 'bg-dark border-dark/20',
    badge: 'Elite',
    badgeColor: 'bg-accent text-dark',
    features: ['Competition-level training', 'Fusion choreography', 'Solo & group performances', 'Personal mentoring by Ravi sir'],
    dark: true,
  },
  {
    level: 'Ladies Special',
    emoji: '💃',
    age: '18 – 50 years',
    timing: 'Sat & Sun — 10:00 AM to 11:30 AM',
    startDate: 'March 8, 2026',
    fee: '₹2,000 / month',
    spots: 12,
    color: 'bg-bg-warm border-primary/15',
    badge: 'Weekend',
    badgeColor: 'bg-primary/15 text-primary',
    features: ['Relaxed learning pace', 'Traditional folk styles', 'Flexibility & fitness', 'Fun group atmosphere'],
  },
];

export default function BatchesSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-hidden').forEach((el, i) => {
              setTimeout(() => el.classList.add('revealed'), i * 100);
            });
          }
        });
      },
      { threshold: 0.08 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="batches" className="py-28 relative overflow-hidden" ref={sectionRef}>
      <div className="absolute inset-0 mandala-bg pointer-events-none" />
      <div className="max-w-7xl mx-auto px-5 relative z-10">
        <div className="text-center mb-16 reveal-hidden">
          <p className="text-primary font-bold uppercase tracking-[0.3em] text-[11px] mb-3">Training Batches</p>
          <h2 className="font-display text-5xl md:text-6xl font-black text-foreground leading-tight">
            Find Your
            <br />
            <span className="font-display italic text-gradient-warm">Perfect Batch.</span>
          </h2>
          <p className="text-muted text-lg mt-4 max-w-xl mx-auto">
            Structured batches for every age and skill level. Limited seats available for personalized attention.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {batches?.map((batch, i) => (
            <div
              key={batch?.level}
              className={`reveal-hidden batch-card rounded-4xl border p-7 flex flex-col ${batch?.color} delay-${i * 100}`}
            >
              <div className="flex items-start justify-between mb-5">
                <span className="text-3xl">{batch?.emoji}</span>
                <span className={`text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full ${batch?.badgeColor}`}>
                  {batch?.badge}
                </span>
              </div>

              <h3 className={`font-display text-2xl font-bold mb-1 ${batch?.dark ? 'text-white' : 'text-foreground'}`}>
                {batch?.level}
              </h3>
              <p className={`text-sm mb-5 ${batch?.dark ? 'text-white/60' : 'text-muted'}`}>{batch?.age}</p>

              <div className={`space-y-2.5 mb-6 text-sm ${batch?.dark ? 'text-white/70' : 'text-muted'}`}>
                <div className="flex items-start gap-2">
                  <Icon name="ClockIcon" size={15} className={`mt-0.5 shrink-0 ${batch?.dark ? 'text-accent' : 'text-primary'}`} />
                  <span>{batch?.timing}</span>
                </div>
                <div className="flex items-start gap-2">
                  <Icon name="CalendarIcon" size={15} className={`mt-0.5 shrink-0 ${batch?.dark ? 'text-accent' : 'text-primary'}`} />
                  <span>Starts {batch?.startDate}</span>
                </div>
                <div className="flex items-start gap-2">
                  <Icon name="UserGroupIcon" size={15} className={`mt-0.5 shrink-0 ${batch?.dark ? 'text-accent' : 'text-primary'}`} />
                  <span className="font-semibold text-red-500">{batch?.spots} seats left</span>
                </div>
              </div>

              <ul className="space-y-2 mb-7 flex-1">
                {batch?.features?.map((f) => (
                  <li key={f} className={`flex items-center gap-2 text-xs ${batch?.dark ? 'text-white/75' : 'text-muted'}`}>
                    <Icon name="CheckCircleIcon" size={14} variant="solid" className={batch?.dark ? 'text-accent shrink-0' : 'text-primary shrink-0'} />
                    {f}
                  </li>
                ))}
              </ul>

              <div className="mt-auto">
                <p className={`font-display text-2xl font-bold mb-4 ${batch?.dark ? 'text-accent' : 'text-primary'}`}>
                  {batch?.fee}
                </p>
                <a
                  href="#register"
                  className={`block text-center text-sm font-bold py-3 rounded-2xl transition-all ${
                    batch?.dark
                      ? 'bg-accent text-dark hover:bg-accent-light' :'bg-primary text-white hover:bg-primary-dark'
                  }`}
                >
                  Enroll in this Batch
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}